import React, { useEffect, useState } from "react";
import clsx from "clsx";
import {
  Box,
  Card,
  Grid,
  makeStyles,
  FormControl,
  TextField,
  Button,
  FormControlLabel,
  FormHelperText,
  Checkbox,
  FormLabel,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import SaveIcon from "@material-ui/icons/Save";
import { NavLink as RouterLink } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { kmMessages } from "src/constant/Messages";
import { DateTimePicker } from "@material-ui/pickers";
import KmTransferList from "src/components/common/KmTransferList";
import Skeleton from "@material-ui/lab/Skeleton";
import moment from "moment";
import { apiEndpoints, apiGetByIdRequest } from "src/utils/api/api";
import KmFilteredDocumentsTable from "src/components/document/KmFilteredDocumentsTable";
import DocumentStructNameSelectOnly from "src/components/document/DocumentStructNameSelectOnly";
import { fillMissingTags } from "src/utils/utils";
import { Typography } from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";

const titleMaxLength = 2000;

const formValidation = {
  required: kmMessages.error.form_validation.field_required,
  maxLength: `${kmMessages.error.form_validation.field_maxlengthnum}`,
  dateChoice: `${kmMessages.error.form_validation.field_datechoice}`,
  votingOptions: `${kmMessages.error.form_validation.field_votinganswechoice}`,
};

const useStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      margin: theme.spacing(2),
    },
  },
  formcontrol: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  paddingbottom: {
    paddingBottom: theme.spacing(1),
  },
  paddingtop: {
    paddingTop: theme.spacing(2),
  },
  button: {
    margin: theme.spacing(1),
  },
}));

const statusToTabs = {
  preparing: "wprzygotowaniu",
  in_progress: "wtrakcie",
  finished: "zakonczone",
  canceled: "anulowane",
};

const DocumentStructRowName = (row, customHandlers, ...rest) => {
  return (
    <DocumentStructNameSelectOnly
      document={row}
      openDocumentHandler={customHandlers.handleOpenDocument}
    />
  );
};

const Form = ({
  formReady,
  answerOptions,
  setAnswerOptions,
  workingGroups,
  selectedWorkingGroup,
  onSubmit,
  voting,
  editMode,
  votingStartDate,
  setVotingStartDate,
  votingEndDate,
  setVotingEndDate,
  ...rest
}) => {
  const classes = useStyles();

  const [autoVotingStart, setAutoVotingStart] = useState(false);
  const [autoVotingEnd, setAutoVotingEnd] = useState(false);

  const [optionsChecked, setOptionsChecked] = useState([]);
  const [optionsRight, setOptionsRight] = useState([]);
  const [afterSubmit, setAfterSubmit] = useState(false);
  const [folderId, setFolderId] = useState("");
  const [documents, setDocuments] = useState([]);
  const [levelDetails, setLevelDetails] = useState({});
  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [documentSelection, setDocumentSelection] = useState(false);

  const [documentNotApplicable, setDocumentNotApplicable] = useState(false);
  const [startStopNotif, setStartStopNotif] = useState(false);
  const [generalVoting, setGeneralVoting] = useState(false);

  const { handleSubmit, setValue, control, errors } = useForm();

  useEffect(() => {
    if (voting) {
      setDocumentNotApplicable(voting.documentNotApplicable);
      setStartStopNotif(voting.startStopNotifications);
      setGeneralVoting(voting.general);
      if (voting.selectedDocumentStructNodeIds?.length > 0) {
        setSelectedDocumentIds(voting.selectedDocumentStructNodeIds);
        setDocumentSelection(true);
      }
      if (!documentNotApplicable) {
        setFolderId(
          voting.documentStructNodeId ? voting.documentStructNodeId : ""
        );
      }
    }
    if (editMode && voting) {
      if (voting.startsAt) {
        setVotingStartDate(moment(voting.startsAt));
      } else {
        setAutoVotingStart(true);
      }
      if (voting.endsAt) {
        setVotingEndDate(moment(voting.endsAt));
      } else {
        setAutoVotingEnd(true);
      }
      setValue("title", voting.title);
      setAnswerOptions(
        answerOptions.filter((answerOption) => {
          return (
            voting.votingOptions.filter(
              (votingOption) => votingOption.answerOptionId === answerOption.id
            ).length === 0
          );
        })
      );
      setOptionsRight(
        answerOptions.filter((answerOption) => {
          return (
            voting.votingOptions.filter(
              (votingOption) => votingOption.answerOptionId === answerOption.id
            ).length !== 0
          );
        })
      );
      if (voting.workingGroup) {
        setValue("wgroup", voting.workingGroup);
      }
      if (voting.selectedDocumentStructNodeIds?.length > 0) {
        setSelectedDocumentIds(voting.selectedDocumentStructNodeIds);
        setDocumentSelection(true);
      }
    }
    return () => {};
  }, [voting, workingGroups]);

  useEffect(() => {
    if (formReady && !documentNotApplicable) {
      apiGetByIdRequest(
        apiEndpoints.getDocuments.path,
        folderId ? folderId : "",
        true
      )
        .then((data) => {
          setDocuments(data.documentStructNodes);

          const { documentStructNodes, ...other } = data;

          setLevelDetails(other);
          if (voting.selectedDocumentStructNodeIds) {
            setSelectedDocumentIds(voting.selectedDocumentStructNodeIds);
          }
        })
        .catch((error) => {});
    }

    return () => {};
  }, [folderId, formReady, documentNotApplicable]);

  useEffect(() => {
    setTimeout(fillMissingTags(), 1000);
    return () => {};
  }, []);

  const handleOpenDocument = (event, documentId) => {
    event.preventDefault();
    setFolderId(documentId);
    setSelectedDocumentIds([]);
  };

  const handleBreadcrumbsItemClick = (event, nodeId) => {
    event.preventDefault();
    setFolderId(nodeId);
    setSelectedDocumentIds([]);
  };

  const onSelectedDocumentIdsChanged = (viewSelectedDocumentIds) => {
    setSelectedDocumentIds(viewSelectedDocumentIds);
    setDocumentSelection(true);
  };

  const headCells = [
    {
      id: "name",
      numeric: false,
      sortable: true,
      fieldName: "id",
      main: false,
      label: "Nazwa",
      customHandlers: { handleOpenDocument },
      component: DocumentStructRowName,
    },
  ];

  const handleVotingStartDateChange = (date) => {
    setVotingStartDate(date);
  };

  const handleAutoVotingStartChange = (event, parentProps) => {
    setVotingStartDate(null);
    setAutoVotingStart(event.target.checked);
    parentProps.onChange(event.target.checked);
  };

  const handleVotingEndDateChange = (date) => {
    setVotingEndDate(date);
  };

  const handleAutoVotingEndChange = (event, parentProps) => {
    setVotingEndDate(null);
    setAutoVotingEnd(event.target.checked);
    parentProps.onChange(event.target.checked);
  };

  const internalOnSubmit = (values) => {
    console.log(optionsRight);
    setAfterSubmit(true);
    if (
      (optionsRight.length >= 2 || voting?.votesExists) &&
      (votingStartDate || autoVotingStart) &&
      (votingEndDate || autoVotingEnd)
    ) {
      onSubmit(
        values,
        optionsRight,
        folderId,
        documentNotApplicable,
        startStopNotif,
        generalVoting,
        selectedDocumentIds
      );
    }
  };

  const handleDocumentNotApplicableChange = (event, parentProps) => {
    setDocumentNotApplicable(event.target.checked);
    parentProps.onChange(event.target.checked);
  };

  const handleStartStopNotifChange = (event, parentProps) => {
    setStartStopNotif(event.target.checked);
    parentProps.onChange(event.target.checked);
  };

  const handleGeneralVotingChange = (event, parentProps) => {
    setGeneralVoting(event.target.checked);
    parentProps.onChange(event.target.checked);
  };

  return (
    <Card className={clsx(classes.root)} {...rest}>
      <Box component="main">
        <Grid container className={classes.root}>
          <Grid item xs={12} elevation={2}>
            {voting?.votesExists ? (
              <Box pb={2}>
                <Alert severity="warning" className={classes.kmmessage}>
                  <AlertTitle>
                    <strong>Uwaga! W głosowaniu oddawano już głosy</strong>
                  </AlertTitle>
                  Edycja możliwa jedynie w zakresie wybranych pól.
                </Alert>
              </Box>
            ) : (
              <></>
            )}
            <form onSubmit={handleSubmit(internalOnSubmit)}>
              {formReady ? (
                voting?.votesExists ? (
                  <Typography>{voting.title}</Typography>
                ) : (
                  <FormControl fullWidth className={classes.formcontrol}>
                    <Controller
                      as={
                        <TextField
                          autoFocus
                          error={errors.title ? true : false}
                          id="title"
                          name="title"
                          label="Tytuł"
                          placeholder="Tytuł"
                          multiline
                          helperText={
                            errors.title &&
                            (
                              formValidation[errors.title.type] || ""
                            ).replaceAll("{maxlen}", titleMaxLength)
                          }
                        />
                      }
                      name="title"
                      defaultValue={voting ? voting.title : ""}
                      control={control}
                      rules={{ required: true, maxLength: titleMaxLength }}
                    />
                  </FormControl>
                )
              ) : (
                <Skeleton />
              )}
              {formReady ? (
                <FormControl fullWidth className={classes.formcontrol}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} sm={6}>
                      <DateTimePicker
                        error={
                          afterSubmit && !votingStartDate && !autoVotingStart
                        }
                        clearable
                        autoOk
                        ampm={false}
                        disabled={autoVotingStart}
                        id="votingstartdate"
                        fullWidth
                        label="Data i godzina rozpoczęcia"
                        clearable
                        value={votingStartDate}
                        placeholder="Data rozpoczęcia głosowania"
                        onChange={(date) => handleVotingStartDateChange(date)}
                        minDate={new Date()}
                        format="DD/MM/yyyy HH:mm:ss"
                        animateYearScrolling
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Controller
                            name="autovotingstart"
                            control={control}
                            defaultValue={autoVotingStart}
                            render={(props) => (
                              <Checkbox
                                {...props}
                                id="autovotingstart"
                                checked={autoVotingStart}
                                onChange={(e) =>
                                  handleAutoVotingStartChange(e, props)
                                }
                                name="autovotingstart"
                                color="primary"
                              />
                            )}
                          />
                        }
                        label="uzupełnienie automatyczne daty i godziny rozpoczęcia głosowania"
                      />
                    </Grid>
                  </Grid>
                  <FormHelperText id="voting-end-date" error>
                    {afterSubmit && !votingStartDate && !autoVotingStart
                      ? formValidation.dateChoice
                      : ""}
                  </FormHelperText>
                </FormControl>
              ) : (
                <Skeleton />
              )}
              {formReady ? (
                <FormControl fullWidth className={classes.formcontrol}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} sm={6}>
                      <DateTimePicker
                        error={afterSubmit && !votingEndDate && !autoVotingEnd}
                        clearable
                        autoOk
                        ampm={false}
                        disabled={autoVotingEnd}
                        id="votingenddate"
                        fullWidth
                        label="Data i godzina zakończenia"
                        clearable
                        value={votingEndDate}
                        placeholder="Data zakończenia głosowania"
                        onChange={(date) => handleVotingEndDateChange(date)}
                        minDate={new Date()}
                        format="DD/MM/yyyy HH:mm"
                        animateYearScrolling
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Controller
                            name="autovotingend"
                            control={control}
                            defaultValue={autoVotingEnd}
                            render={(props) => (
                              <Checkbox
                                {...props}
                                id="autovotingend"
                                checked={autoVotingEnd}
                                onChange={(e) =>
                                  handleAutoVotingEndChange(e, props)
                                }
                                name="autovotingend"
                                color="primary"
                              />
                            )}
                          />
                        }
                        label="uzupełnienie automatyczne daty i godziny zakończenia głosowania"
                      />
                    </Grid>
                  </Grid>
                  <FormHelperText id="voting-end-date" error>
                    {afterSubmit && !votingEndDate && !autoVotingEnd
                      ? formValidation.dateChoice
                      : ""}
                  </FormHelperText>
                </FormControl>
              ) : (
                <Skeleton />
              )}
              {formReady ? (
                voting?.votesExists ? (
                  <></>
                ) : (
                  <FormControl fullWidth className={classes.formcontrol}>
                    <Box className={classes.paddingbottom}>
                      <FormLabel>Warianty odpowiedzi:</FormLabel>
                    </Box>
                    <KmTransferList
                      errorState={afterSubmit && optionsRight.length < 2}
                      checked={optionsChecked}
                      setChecked={setOptionsChecked}
                      left={answerOptions}
                      setLeft={setAnswerOptions}
                      right={optionsRight}
                      setRight={setOptionsRight}
                    />
                    <FormHelperText id="voting-end-date" error>
                      {afterSubmit && optionsRight.length < 2
                        ? formValidation.votingOptions
                        : ""}
                    </FormHelperText>
                  </FormControl>
                )
              ) : (
                <Skeleton />
              )}

              {formReady && workingGroups?.length > 0 ? (
                voting?.votesExists ? (
                  <></>
                ) : (
                  <FormControl fullWidth className={classes.formcontrol}>
                    <Controller
                      render={({ onChange, ...props }) => (
                        <Autocomplete
                          id="working-group"
                          options={workingGroups}
                          getOptionLabel={(option) => option.name}
                          getOptionSelected={(option, value) =>
                            value && option.id === value.id
                          }
                          value={props.value}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={errors.wgroup ? true : false}
                              label="Grupa robocza"
                              placeholder="Grupa robocza"
                              onChange={(e, data) => onChange(data)}
                              helperText={
                                errors.wgroup &&
                                (formValidation[errors.wgroup.type] || "")
                              }
                            />
                          )}
                          onChange={(e, data) => onChange(data)}
                        />
                      )}
                      onChange={([, data]) => data}
                      name="wgroup"
                      control={control}
                      defaultValue={voting ? voting.workingGroup : null}
                    />
                  </FormControl>
                )
              ) : (
                <Skeleton />
              )}
              {formReady ? (
                <FormControl fullWidth className={classes.formcontrol}>
                  <FormControlLabel
                    control={
                      <Controller
                        name="startstopnotif"
                        control={control}
                        defaultValue={startStopNotif}
                        render={(props) => (
                          <Checkbox
                            {...props}
                            id="startstopnotif"
                            checked={startStopNotif}
                            onChange={(e) =>
                              handleStartStopNotifChange(e, props)
                            }
                            name="startstopnotif"
                            color="primary"
                          />
                        )}
                      />
                    }
                    label="wyślij e-mail o rozpoczęciu i zakończeniu głosowania"
                  />
                </FormControl>
              ) : (
                <Skeleton />
              )}
              {formReady ? (
                <FormControl fullWidth className={classes.formcontrol}>
                  <FormControlLabel
                    control={
                      <Controller
                        name="generalvoting"
                        control={control}
                        defaultValue={generalVoting}
                        render={(props) => (
                          <Checkbox
                            {...props}
                            id="generalvoting"
                            checked={generalVoting}
                            onChange={(e) =>
                              handleGeneralVotingChange(e, props)
                            }
                            name="generalvoting"
                            color="primary"
                          />
                        )}
                      />
                    }
                    label="głosowanie ogólne (głosują wszyscy posiadający uprawnienie głosowania)"
                  />
                </FormControl>
              ) : (
                <Skeleton />
              )}
              {formReady ? (
                <FormControl fullWidth className={classes.formcontrol}>
                  <Box className={classes.paddingbottom}>
                    <FormLabel>Dokumenty:</FormLabel>
                  </Box>
                  <FormControlLabel
                    control={
                      <Controller
                        name="documentnotapplicable"
                        control={control}
                        defaultValue={documentNotApplicable}
                        render={(props) => (
                          <Checkbox
                            {...props}
                            id="autovotingend"
                            checked={documentNotApplicable}
                            onChange={(e) =>
                              handleDocumentNotApplicableChange(e, props)
                            }
                            name="documentnotapplicable"
                            color="primary"
                          />
                        )}
                      />
                    }
                    label="nie dotyczy"
                  />
                  {documentSelection && !documentNotApplicable ? (
                    <Alert severity="warning" className={classes.massagebox}>
                      <div>
                        <b>Uwaga! </b> Przechodzenie pomiędzy katalogami usuwa
                        poprzednią selekcję pojedynczych dokumentów. Pojedyncze
                        dokumenty mogą być wskazane jedynie w obrębie tego
                        samego katalogu.
                      </div>
                    </Alert>
                  ) : (
                    <></>
                  )}
                  {!documentNotApplicable ? (
                    <KmFilteredDocumentsTable
                      levelDetails={levelDetails}
                      markable={true}
                      markableRowType="1"
                      searchable={false}
                      headCells={headCells}
                      rows={documents}
                      defaultOrder="asc"
                      onBreadcrumbsItemClick={handleBreadcrumbsItemClick}
                      onSelectedRowIdsChange={onSelectedDocumentIdsChanged}
                      disableAttendanceChecking={true}
                      defaultSelected={selectedDocumentIds}
                    />
                  ) : (
                    <></>
                  )}
                </FormControl>
              ) : (
                <Skeleton />
              )}
              <Box className={classes.paddingtop}>
                <Button
                  color="primary"
                  className={classes.button}
                  component={RouterLink}
                  to={`/zarzadzanieglosowania/${
                    voting && statusToTabs[voting.currentStatus]
                      ? statusToTabs[voting.currentStatus]
                      : "wprzygotowaniu"
                  }`}
                >
                  Anuluj
                </Button>
                <Button
                  disabled={!formReady}
                  color="primary"
                  variant="contained"
                  className={classes.button}
                  endIcon={<SaveIcon />}
                  type="submit"
                >
                  Zapisz
                </Button>
              </Box>
            </form>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

export default Form;
